import AppsList from "components/AppsList"
import { event } from "utils/gtag"
import useT from "utils/useT"
import useQueryPlus from "utils/useQueryPlus"
import useDebounce from "utils/useDebounce"
import useSessionStore from "stores/session"
import { AnimatePresence, motion } from "framer-motion"
import useRouterStore from "stores/routerStore"
import { useEffect } from "react"

interface Props {
  serverQuery?: any
}

export const Search = ({ serverQuery }: Props) => {
  const t = useT()
  const query = useRouterStore((state) => state.router?.query)
  const search =
    typeof query?.search === "string" ? query?.search : serverQuery?.search
  const push = useRouterStore((state) => state.router?.push)
  const pathname = useRouterStore((state) => state.router?.pathname)
  const asPath = useRouterStore((state) => state.router?.asPath)
  const debouncedSearch = useDebounce(search, search ? 1200 : 0)
  const isAdmin = useSessionStore((state) => state.user.isAdmin)
  const {
    data: results,
    isPending,
    fetchStatus,
  } = useQueryPlus({
    enabled: !!debouncedSearch,
    queryKey: ["search", debouncedSearch],
    staleTime: 5 * 60 * 1000, // 5min
    gcTime: 5 * 60 * 1000, // 5min
    queryFn: () =>
      fetch(
        `/api/search?term=${debouncedSearch}&skipSaveSearch=${
          isAdmin ? "true" : ""
        }`,
      ).then((res) => res.json()),
  })

  useEffect(() => {
    event({
      action: "search_input",
      category: "Search",
      label: "Search Request",
      value: debouncedSearch,
    })
  }, [results])

  const loading = isPending && fetchStatus !== "idle"

  const handleSearch = (search: string) => {
    push?.(
      {
        pathname,
        query: {
          ...query,
          search,
        },
      },
      {
        pathname: asPath?.split("?")[0],
        query: {
          ...query,
          search,
        },
      },
      { shallow: true },
    )
  }
  return (
    <>
      <div className="card card-style mb-2">
        <div className="content">
          <p dir="auto">{t.search.hero}</p>
        </div>
      </div>
      <div className="content searchbox position-relative">
        <div className="search-box search-color bg-highlight rounded-m mb-3 d-flex align-items-center">
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                key="search-loading"
                transition={{ duration: 0.075 }}
                initial={{ scale: 0 }}
                animate={{ scale: 0.6 }}
                exit={{ scale: 0 }}
                style={{ transform: "scale(0.6)" }}
                className="d-flex ps-2 position-absolute"
              >
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </motion.div>
            ) : (
              <motion.i
                key="search-icon"
                transition={{ duration: 0.075 }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="fa fa-search"
              ></motion.i>
            )}
          </AnimatePresence>

          <input
            type="text"
            dir="auto"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="border-0"
            placeholder={t.search.search}
            data-search=""
          />
          {search && (
            <i
              onClick={() => handleSearch("")}
              style={{ cursor: "pointer" }}
              className="fa fa-times p-3 pe-4"
            ></i>
          )}
        </div>
      </div>
      {results?.length > 0 ? (
        <AppsList apps={results} title="" />
      ) : (
        <div className="search-no-results content">
          <div className="card card-style mx-0">
            <div className="content">
              <h4 dir="auto">{t.search.noResult}</h4>
              <p dir="auto">{t.search.noResultSub}</p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
export default Search
